import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { http, formatIDR, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Sparkle, Target, ChartLineUp, Trophy, Briefcase, User, Buildings, ArrowSquareOut } from "@phosphor-icons/react";

const stageColor = {
  lead: "#5A6B76",
  quotation: "#0284C7",
  discuss_mgmt: "#7C3AED",
  negotiation: "#4F46E5",
  invoice: "#D97706",
  payment: "#16A34A",
  pending: "#F59E0B",
  lose: "#DC2626",
};
const OPEN_STAGES = [
  "lead",
  "quotation",
  "discuss_mgmt",
  "negotiation",
  "invoice",
  "pending",
];

export default function ForecastPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOwner, setActiveOwner] = useState("all"); // 'all' or owner_id, for admin/manager
  const [expandedStage, setExpandedStage] = useState("quotation");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get("/analytics/forecast");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const isTeamView = data?.scope === "team";

  // Filtered stage bucket by owner
  const filteredBucket = useMemo(() => {
    if (!data) return {};
    if (activeOwner === "all") return data.stage_bucket;
    const out = {};
    for (const stg of OPEN_STAGES) {
      out[stg] = (data.stage_bucket[stg] || []).filter((d) => d.owner_id === activeOwner);
    }
    return out;
  }, [data, activeOwner]);

  // Filtered totals when a specific owner selected
  const scopedTotals = useMemo(() => {
    if (!data) return null;
    if (activeOwner === "all") return data.totals;
    const row = data.per_owner.find((r) => r.owner_id === activeOwner);
    if (!row) return data.totals;
    return {
      weighted: row.weighted,
      commit: row.commit,
      best_case: row.open_value,
      won_value: row.won_value,
      projected_total: row.weighted + row.won_value,
      open_count: row.open_count,
      won_count: row.won_count,
    };
  }, [data, activeOwner]);

  if (loading || !data) {
    return <AppLayout><div className="text-sm text-[#5A6B76] uppercase tracking-[0.15em]">Memuat forecast...</div></AppLayout>;
  }

  const activeOwnerName = activeOwner === "all"
    ? (isTeamView ? "Seluruh Tim" : user?.name)
    : (data.per_owner.find((r) => r.owner_id === activeOwner)?.owner_name || "-");

  return (
    <AppLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Sales Forecasting</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]" data-testid="forecast-title">Forecast Detail</h1>
          <p className="text-[#64748B] mt-1 text-xs sm:text-sm">
            {isTeamView
              ? "Ringkasan forecast per sales & rincian deal yang berkontribusi."
              : "Forecast untuk klien yang Anda kelola. Nilai weighted = nilai deal × probabilitas."}
          </p>
        </div>
        <div className="pt-2 sm:pt-0 border-t sm:border-t-0 border-[#E2E8F0] sm:text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold">Cakupan</div>
          <div className="font-mono-data text-xs sm:text-base font-semibold text-[#173042]" data-testid="forecast-scope">{activeOwnerName}</div>
        </div>
      </div>

      {/* Forecast summary strip */}
      <div className="bg-[#173B4D] text-white p-4 sm:p-6 lg:p-8 rounded-xl border border-[#24566D]/50 grid grid-cols-2 lg:grid-cols-5 gap-3.5 sm:gap-6 relative overflow-hidden mb-5 sm:mb-6 shadow-xs" data-testid="forecast-summary">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full brand-dot-accent opacity-20" />
        <SummaryTile testid="fc-total-weighted" label="Weighted Pipeline" icon={ChartLineUp} value={formatIDR(scopedTotals.weighted)} />
        <SummaryTile testid="fc-total-commit" label="Commit (Prop + Nego)" icon={Target} value={formatIDR(scopedTotals.commit)} />
        <SummaryTile testid="fc-total-best" label="Best Case" icon={Briefcase} value={formatIDR(scopedTotals.best_case)} />
        <SummaryTile testid="fc-total-won" label="Won Bulan Ini" icon={Trophy} value={formatIDR(scopedTotals.won_value)} />
        <div className="col-span-2 sm:col-span-1">
          <SummaryTile testid="fc-total-projected" label="Proyeksi Total" icon={Sparkle} value={formatIDR(scopedTotals.projected_total)} highlight />
        </div>
      </div>

      {/* Per-sales breakdown (admin+manager only) */}
      {isTeamView && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-6 mb-5 sm:mb-6 shadow-2xs" data-testid="forecast-per-owner">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <h3 className="font-display text-lg sm:text-2xl tracking-tight text-[#173042]">Forecast per Sales</h3>
              <div className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-[0.12em] font-semibold mt-0.5">Klik baris untuk memfilter deal di bawah</div>
            </div>
            <button
              onClick={() => setActiveOwner("all")}
              data-testid="filter-all-owners"
              className={`self-start sm:self-auto text-xs uppercase tracking-[0.12em] font-semibold px-3 py-1.5 rounded-lg border transition-colors ${activeOwner === "all" ? "bg-[#173B4D] text-white border-[#173B4D]" : "border-[#CBD5E1] text-[#64748B] hover:border-[#173B4D]"}`}
            >
              Semua Sales
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
            <table className="w-full min-w-[700px]" data-testid="per-owner-table">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <Th>Sales</Th>
                  <Th className="text-right">Open</Th>
                  <Th className="text-right">Best Case</Th>
                  <Th className="text-right">Weighted</Th>
                  <Th className="text-right">Commit</Th>
                  <Th className="text-right">Won</Th>
                  <Th className="text-right">Proyeksi</Th>
                </tr>
              </thead>
              <tbody>
                {data.per_owner.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-sm text-[#64748B]">Belum ada data.</td></tr>
                )}
                {data.per_owner.map((r) => {
                  const active = activeOwner === r.owner_id;
                  const projected = r.weighted + r.won_value;
                  return (
                    <tr
                      key={r.owner_id}
                      data-testid={`owner-row-${r.owner_id}`}
                      onClick={() => setActiveOwner(active ? "all" : r.owner_id)}
                      className={`border-b border-[#E2E8F0] cursor-pointer transition-colors ${active ? "bg-[#F1F5F9]" : "hover:bg-[#F8FAFC]"}`}
                    >
                      <Td>
                        <div className="flex items-center gap-2.5">
                          {r.owner_avatar ? (
                            <img src={r.owner_avatar} alt={r.owner_name} className="w-7 h-7 rounded-full object-cover border border-[#CBD5E1]" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-[#173B4D] text-white flex items-center justify-center text-xs font-bold shrink-0">
                              {r.owner_name?.[0] || "?"}
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-xs text-[#173042]">{r.owner_name}</div>
                            <div className="text-[9px] uppercase tracking-[0.1em] text-[#64748B]">{r.owner_role}</div>
                          </div>
                        </div>
                      </Td>
                      <Td className="text-right font-mono-data text-xs">{r.open_count}</Td>
                      <Td className="text-right font-mono-data text-xs">{formatIDR(r.open_value)}</Td>
                      <Td className="text-right font-mono-data text-xs text-[#D9A321] font-bold">{formatIDR(r.weighted)}</Td>
                      <Td className="text-right font-mono-data text-xs text-[#1C4E63]">{formatIDR(r.commit)}</Td>
                      <Td className="text-right font-mono-data text-xs text-[#2E8B57]">{formatIDR(r.won_value)}</Td>
                      <Td className="text-right font-mono-data text-xs font-bold text-[#173042]">{formatIDR(projected)}</Td>
                    </tr>
                  );
                })}
              </tbody>
              {data.per_owner.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[#173B4D] bg-[#F8FAFC]">
                    <Td className="font-bold uppercase tracking-[0.1em] text-[10px]">Total</Td>
                    <Td className="text-right font-mono-data text-xs font-bold">{data.per_owner.reduce((s, r) => s + r.open_count, 0)}</Td>
                    <Td className="text-right font-mono-data text-xs font-bold">{formatIDR(data.per_owner.reduce((s, r) => s + r.open_value, 0))}</Td>
                    <Td className="text-right font-mono-data text-xs text-[#D9A321] font-bold">{formatIDR(data.per_owner.reduce((s, r) => s + r.weighted, 0))}</Td>
                    <Td className="text-right font-mono-data text-xs text-[#1C4E63] font-bold">{formatIDR(data.per_owner.reduce((s, r) => s + r.commit, 0))}</Td>
                    <Td className="text-right font-mono-data text-xs text-[#2E8B57] font-bold">{formatIDR(data.per_owner.reduce((s, r) => s + r.won_value, 0))}</Td>
                    <Td className="text-right font-mono-data text-xs font-bold text-[#173042]">{formatIDR(data.per_owner.reduce((s, r) => s + r.weighted + r.won_value, 0))}</Td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Stage buckets with deals */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-6 shadow-2xs" data-testid="forecast-stage-buckets">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display text-lg sm:text-2xl tracking-tight text-[#173042]">Deal per Tahap</h3>
            <div className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-[0.12em] font-semibold mt-0.5">
              Rincian deal yang membentuk forecast {activeOwner === "all" ? "" : `— ${activeOwnerName}`}
            </div>
          </div>
          <div className="flex flex-wrap gap-1 bg-[#F1F5F9] p-1 rounded-lg border border-[#E2E8F0]">
            {OPEN_STAGES.map((s) => {
              const count = (filteredBucket[s] || []).length;
              const active = expandedStage === s;
              return (
                <button
                  key={s}
                  onClick={() => setExpandedStage(s)}
                  data-testid={`stage-tab-${s}`}
                  className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.08em] font-semibold rounded-md transition-colors flex items-center gap-1.5 ${
                    active ? "bg-[#173B4D] text-white shadow-2xs" : "text-[#64748B] hover:text-[#173042]"
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "#D9A321" : stageColor[s] }} />
                  {t(`stage.${s}`)}
                  <span className="font-mono-data opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                <Th>Deal</Th>
                {isTeamView && activeOwner === "all" && <Th>Sales</Th>}
                <Th className="text-right">Nilai</Th>
                <Th className="text-right">Probabilitas</Th>
                <Th className="text-right">Weighted</Th>
                <Th>Diperbarui</Th>
                <Th className="text-right">Aksi</Th>
              </tr>
            </thead>
            <tbody data-testid={`stage-deals-${expandedStage}`}>
              {(filteredBucket[expandedStage] || []).length === 0 && (
                <tr><td colSpan={isTeamView && activeOwner === "all" ? 7 : 6} className="text-center py-12 text-sm text-[#64748B]">
                  Tidak ada deal di tahap {t(`stage.${expandedStage}`)}.
                </td></tr>
              )}
              {(filteredBucket[expandedStage] || []).map((d) => (
                <tr key={d.id} data-testid={`fc-deal-${d.id}`} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors">
                  <Td>
                    <div className="font-semibold text-xs sm:text-sm text-[#173042]">{d.title}</div>
                    {d.company && (
                      <div className="flex items-center gap-1 text-[11px] text-[#64748B] mt-0.5"><Buildings size={12} /> {d.company}</div>
                    )}
                  </Td>
                  {isTeamView && activeOwner === "all" && (
                    <Td>
                      <div className="inline-flex items-center gap-1 text-xs text-[#64748B]"><User size={12} /> {d.owner_name}</div>
                    </Td>
                  )}
                  <Td className="text-right font-mono-data text-xs sm:text-sm">{formatIDR(d.value)}</Td>
                  <Td className="text-right font-mono-data text-xs text-[#64748B]">{d.probability}%</Td>
                  <Td className="text-right font-mono-data text-xs sm:text-sm font-bold text-[#D9A321]">{formatIDR(d.weighted)}</Td>
                  <Td className="font-mono-data text-[11px] text-[#64748B]">{formatDate(d.updated_at)}</Td>
                  <Td className="text-right">
                    <Link to="/pipeline" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.1em] font-semibold text-[#0F7180] hover:text-[#173B4D]">
                      Buka <ArrowSquareOut size={12} weight="bold" />
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
            {(filteredBucket[expandedStage] || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#173B4D] bg-[#F8FAFC]">
                  <Td className="font-bold uppercase tracking-[0.1em] text-[10px]" colSpan={isTeamView && activeOwner === "all" ? 2 : 1}>
                    Subtotal
                  </Td>
                  <Td className="text-right font-mono-data text-xs font-bold">{formatIDR((filteredBucket[expandedStage] || []).reduce((s, d) => s + d.value, 0))}</Td>
                  <Td></Td>
                  <Td className="text-right font-mono-data text-xs font-bold text-[#D9A321]">{formatIDR((filteredBucket[expandedStage] || []).reduce((s, d) => s + d.weighted, 0))}</Td>
                  <Td colSpan={2}></Td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </AppLayout>
  );
}

function SummaryTile({ label, value, icon: Icon, highlight, testid }) {
  return (
    <div className="relative z-10" data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-bold ${highlight ? "text-[#D9A321]" : "text-white/70"}`}>
        <Icon size={14} weight={highlight ? "fill" : "regular"} className="shrink-0" /> <span className="truncate">{label}</span>
      </div>
      <div className={`font-mono-data mt-1 sm:mt-1.5 leading-tight font-bold truncate ${highlight ? "text-xl sm:text-2xl lg:text-3xl text-[#D9A321]" : "text-base sm:text-xl lg:text-2xl text-white"}`}>{value}</div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-[#5A6B76] ${className}`}>{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`py-3 px-4 text-sm text-[#0F2A3A] ${className}`}>{children}</td>;
